"use client";

/**
 * Prefer-over DialKit Slider — only import dialkit Slider from this module (C14/C18).
 * Embedded mounts host `.dialkit-root` so vendor theme tokens resolve outside DialRoot.
 * P13.C / C25: optional --dk-track / --dk-range / --dk-thumb paint overrides.
 * P13.F: vertical = Prefer-over pointer SoT (vendor has no vertical orientation).
 */

import { Slider as DialKitVendorSlider } from "dialkit";
import type { CSSProperties, PointerEvent as ReactPointerEvent } from "react";
import { useCallback, useRef } from "react";
import { cn } from "../../../lib";
import { DialKitEmbeddedHost } from "./dialkit-embedded-host";
import "./dialkit-styles";
import "./dialkit-slider-theme.css";

export interface DialKitSliderProps {
	label: string;
	value: number;
	onChange: (value: number) => void;
	min?: number;
	max?: number;
	step?: number;
	unit?: string;
	disabled?: boolean;
	className?: string;
	/** When true, hides vendor label/value chrome (FieldType supplies its own). */
	hideLabel?: boolean;
	/**
	 * D21 / P13.F — vertical HUD / map zoom.
	 * Prefer-over owns vertical pointer mapping (max at top).
	 */
	orientation?: "horizontal" | "vertical";
	/** Vertical track length in CSS (default 8rem / h-32). Ignored for horizontal. */
	verticalLengthClassName?: string;
	onPointerDown?: (event: ReactPointerEvent<HTMLDivElement>) => void;
	onLostPointerCapture?: (event: ReactPointerEvent<HTMLDivElement>) => void;
	/** P13.C — Prefer-over track paint (maps to --dk-track). */
	trackClassName?: string;
	rangeClassName?: string;
	thumbClassName?: string;
	trackStyle?: CSSProperties;
	rangeStyle?: CSSProperties;
	thumbStyle?: CSSProperties;
}

function cssColor(
	style: CSSProperties | undefined,
	key: keyof CSSProperties,
): string | undefined {
	const v = style?.[key];
	return typeof v === "string" ? v : undefined;
}

function buildPaintStyle(params: {
	trackStyle?: CSSProperties;
	rangeStyle?: CSSProperties;
	thumbStyle?: CSSProperties;
}): CSSProperties | undefined {
	const trackBg = cssColor(params.trackStyle, "backgroundColor");
	const rangeBg = cssColor(params.rangeStyle, "backgroundColor");
	const thumbBg = cssColor(params.thumbStyle, "backgroundColor");
	const thumbBorder = cssColor(params.thumbStyle, "borderColor");
	const thumbBorderStyle = cssColor(params.thumbStyle, "borderStyle");
	const thumbBorderWidth =
		typeof params.thumbStyle?.borderWidth === "number"
			? `${params.thumbStyle.borderWidth}px`
			: typeof params.thumbStyle?.borderWidth === "string"
				? params.thumbStyle.borderWidth
				: undefined;

	if (
		!trackBg &&
		!rangeBg &&
		!thumbBg &&
		!thumbBorder &&
		!thumbBorderStyle &&
		!thumbBorderWidth
	) {
		return undefined;
	}

	return {
		...(trackBg ? { ["--dk-track" as string]: trackBg } : {}),
		...(rangeBg ? { ["--dk-range" as string]: rangeBg } : {}),
		...(thumbBg ? { ["--dk-thumb" as string]: thumbBg } : {}),
		...(thumbBorder
			? { ["--dk-thumb-border" as string]: thumbBorder }
			: {}),
		...(thumbBorderStyle
			? { ["--dk-thumb-border-style" as string]: thumbBorderStyle }
			: {}),
		...(thumbBorderWidth
			? { ["--dk-thumb-border-width" as string]: thumbBorderWidth }
			: {}),
	};
}

function clamp(n: number, lo: number, hi: number): number {
	return Math.min(hi, Math.max(lo, n));
}

function snapToStep(n: number, min: number, max: number, step: number): number {
	if (!(step > 0)) {
		return clamp(n, min, max);
	}
	const snapped = Math.round((n - min) / step) * step + min;
	return clamp(Number(snapped.toFixed(6)), min, max);
}

/**
 * Prefer-over DialKit Slider.
 */
export function DialKitSlider({
	label,
	value,
	onChange,
	min = 0,
	max = 1,
	step = 0.01,
	unit,
	disabled = false,
	className,
	hideLabel = false,
	orientation = "horizontal",
	verticalLengthClassName = "h-32",
	onPointerDown,
	onLostPointerCapture,
	trackClassName,
	rangeClassName,
	thumbClassName,
	trackStyle,
	rangeStyle,
	thumbStyle,
}: DialKitSliderProps) {
	const isVertical = orientation === "vertical";
	const paintStyle = buildPaintStyle({ trackStyle, rangeStyle, thumbStyle });
	const hasPaint =
		Boolean(paintStyle) ||
		Boolean(trackClassName) ||
		Boolean(rangeClassName) ||
		Boolean(thumbClassName);
	const trackRef = useRef<HTMLDivElement>(null);

	const valueFromClientY = useCallback(
		(clientY: number) => {
			const el = trackRef.current;
			if (!el) {
				return value;
			}
			const rect = el.getBoundingClientRect();
			if (rect.height <= 0) {
				return value;
			}
			/** Max at top (HUD volume/speed convention). */
			const t = 1 - (clientY - rect.top) / rect.height;
			const raw = min + clamp(t, 0, 1) * (max - min);
			return snapToStep(raw, min, max, step);
		},
		[max, min, step, value],
	);

	if (!isVertical) {
		return (
			<DialKitEmbeddedHost
				data-dialkit-slider=""
				data-orientation="horizontal"
				data-disabled={disabled ? "true" : undefined}
				data-dk-paint={hasPaint ? "true" : undefined}
				data-dk-hide-chrome={hideLabel ? "true" : undefined}
				data-dk-track-class={trackClassName ?? undefined}
				data-dk-range-class={rangeClassName ?? undefined}
				data-dk-thumb-class={thumbClassName ?? undefined}
				className={cn(
					"min-w-0",
					disabled && "pointer-events-none opacity-50",
					trackClassName,
					rangeClassName,
					thumbClassName,
					className,
				)}
				style={paintStyle}
				aria-disabled={disabled || undefined}
				onPointerDown={onPointerDown}
				onLostPointerCapture={onLostPointerCapture}
			>
				<DialKitVendorSlider
					label={hideLabel ? "\u00a0" : label}
					value={value}
					onChange={onChange}
					min={min}
					max={max}
					step={step}
					{...(unit ? { unit } : {})}
				/>
			</DialKitEmbeddedHost>
		);
	}

	const span = max - min;
	const pct = span === 0 ? 0 : clamp((value - min) / span, 0, 1);
	const fillHeightPct = `${pct * 100}%`;
	const thumbBottomPct = `${pct * 100}%`;

	return (
		<DialKitEmbeddedHost
			ref={trackRef}
			data-dialkit-slider=""
			data-orientation="vertical"
			data-vertical-pointer-sot="true"
			data-disabled={disabled ? "true" : undefined}
			data-dk-paint={hasPaint ? "true" : undefined}
			role="slider"
			aria-orientation="vertical"
			aria-valuemin={min}
			aria-valuemax={max}
			aria-valuenow={value}
			aria-label={label}
			aria-disabled={disabled || undefined}
			tabIndex={disabled ? -1 : 0}
			className={cn(
				"relative flex w-10 shrink-0 touch-none select-none items-stretch justify-center overflow-visible",
				verticalLengthClassName,
				disabled && "pointer-events-none opacity-50",
				trackClassName,
				rangeClassName,
				thumbClassName,
				className,
			)}
			style={paintStyle}
			onPointerDown={(event) => {
				onPointerDown?.(event);
				if (disabled || event.button !== 0) {
					return;
				}
				event.currentTarget.setPointerCapture(event.pointerId);
				onChange(valueFromClientY(event.clientY));
			}}
			onPointerMove={(event) => {
				if (disabled) {
					return;
				}
				if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
					return;
				}
				onChange(valueFromClientY(event.clientY));
			}}
			onLostPointerCapture={(event) => {
				onLostPointerCapture?.(event);
			}}
			onKeyDown={(event) => {
				if (disabled) {
					return;
				}
				const delta = step > 0 ? step : 1;
				if (event.key === "ArrowUp" || event.key === "ArrowRight") {
					event.preventDefault();
					onChange(Math.min(max, value + delta));
				} else if (
					event.key === "ArrowDown" ||
					event.key === "ArrowLeft"
				) {
					event.preventDefault();
					onChange(Math.max(min, value - delta));
				} else if (event.key === "Home") {
					event.preventDefault();
					onChange(min);
				} else if (event.key === "End") {
					event.preventDefault();
					onChange(max);
				}
			}}
		>
			{/* Prefer-over vertical track (P13.F) — vendor has no vertical orientation. */}
			<div
				className="relative mx-auto h-full w-2 rounded-full"
				data-dialkit-vertical-track
				style={{
					background: "var(--dk-track, var(--dial-surface))",
				}}
			>
				<div
					className="absolute bottom-0 left-0 w-full rounded-full"
					data-dialkit-vertical-fill
					style={{
						height: fillHeightPct,
						background:
							"var(--dk-range, var(--dial-surface-active))",
					}}
				/>
				<div
					className="absolute left-1/2 size-4 -translate-x-1/2 translate-y-1/2 rounded-full border-2 shadow"
					data-dialkit-vertical-thumb
					style={{
						bottom: thumbBottomPct,
						background: "var(--dk-thumb, var(--dial-text-primary))",
						borderColor:
							"var(--dk-thumb-border, var(--dial-glass-bg))",
					}}
				/>
			</div>
		</DialKitEmbeddedHost>
	);
}
